import React, {
  RefObject,
  useRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import moment from "moment";
import cn from "classnames";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  MicrophoneIcon,
  StopIcon,
  PauseIcon,
  PlayIcon,
  ArrowUpTrayIcon,
  FolderArrowDownIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { defaultRecordingName, SaveAudioType } from "./uploadFileConfig";
import { useAuth } from "../../context/AuthContext";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import { useAudioEditor } from "../../hooks/useAudioEditor";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import {
  validateAudioFile,
  type AudioUploadOptions,
} from "../../utils/audioUtils";
import Button from "../common/Button";
import Toggle from "../Settings/Toggle";
import RecordingVisualizer from "./RecordingVisualizer";
import AudioWaveformEditor, {
  AudioWaveformEditorHandle,
} from "./AudioWaveformEditor";
import TemplateSelector from "../common/TemplateSelector";
import type { Suggestion } from "../../db/schema";
import "./AudioPlayback.css";
import SuggestionPoint from "../Editor/SuggestionPoint";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/**
 * Side-by-side PDF panel shown next to the recording controls when a `pdfUrl`
 * is provided (e.g. recording audio to fill a PDF form). Lets the user read the
 * form and page through it while they narrate.
 */
function RecordPdfPanel({ pdfUrl }: { pdfUrl: string }) {
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);

  return (
    <div className="flex flex-col h-[67.5vh] rounded-lg border border-slate-200 dark:border-base-300 bg-gray-100 dark:bg-base-200 overflow-hidden">
      <div className="flex-1 overflow-y-auto bg-white">
        <Document
          file={pdfUrl}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            setPage(1);
          }}
          loading={
            <div className="flex items-center justify-center h-full min-h-[300px]">
              <span className="loading loading-spinner loading-md text-base-content/30" />
            </div>
          }
          error={
            <div className="flex items-center justify-center text-base-content/40 text-sm h-full min-h-[300px]">
              Failed to load PDF
            </div>
          }
        >
          <Page
            pageNumber={page}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="[&_canvas]:!w-full [&_canvas]:!h-auto"
            loading={<span />}
          />
        </Document>
      </div>
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 py-2 bg-gray-50 dark:bg-base-200 border-t border-gray-200 dark:border-base-300">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-base-300 disabled:opacity-30 transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
          </button>
          <span className="text-xs text-gray-500 dark:text-base-content/60 font-medium min-w-[5rem] text-center">
            Page {page} of {numPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            disabled={page >= numPages}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-base-300 disabled:opacity-30 transition-colors"
            aria-label="Next page"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
          </button>
        </div>
      )}
    </div>
  );
}

interface AudioPlaybackProps {
  modalRef: RefObject<HTMLDialogElement>;
  handleAudioFileChange: (
    fileOrEvent: File | Blob | React.ChangeEvent<HTMLInputElement>,
    audioType?: string,
    methodOrOptions?: string | AudioUploadOptions,
    templateId?: string | null,
  ) => Promise<void>;
  onAddToUpload?: (blob: Blob) => void;
  // Adds a "Save to Files" action next to Upload: the recording goes to the
  // Files library as a narrative, with no report created.
  onSaveToFiles?: (blob: Blob, name?: string, type?: SaveAudioType) => void;
  addToUploadMode?: boolean;
  // Shows the Report Settings accordion (template, detail level, timestamps)
  // like UploadModal — for the main record-a-report flow
  showReportSettings?: boolean;
  // Suggestions mode props. Narrowed shape (just the fields the list
  // renders) so callers can pass either a full Suggestion[] from the DB or a
  // trimmed projection like UploadModal does.
  suggestionsMode?: boolean;
  suggestions?: Array<
    Pick<Suggestion, "id" | "content" | "implemented">
  >;
  placeholders?: string[];
  // Hands the recorded WAV blob (and optional template ID) to the parent for processing.
  onSuggestionsRecorded?: (wavBlob: Blob, reportTemplateId?: string | null) => void;
  // When set, shows a side-by-side PDF viewer (with page navigation) so the
  // user can read/cycle through a form while recording.
  pdfUrl?: string;
}

export default function AudioPlayback({
  modalRef,
  handleAudioFileChange,
  onAddToUpload,
  onSaveToFiles,
  addToUploadMode = false,
  showReportSettings = false,
  suggestionsMode = false,
  suggestions: suggestionsList,
  placeholders,
  onSuggestionsRecorded,
  pdfUrl,
}: AudioPlaybackProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const editorRef = useRef<AudioWaveformEditorHandle>(null);
  const closeConfirmRef = useRef<HTMLDivElement>(null);
  const clearConfirmRef = useRef<HTMLDivElement>(null);
  const insertAtRef = useRef<number | null>(null);
  const [waveformContainer, setWaveformContainer] =
    useState<HTMLDivElement | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [templateKey, setTemplateKey] = useState(0);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(false);
  // Per-report call sign override — defaults to the saved preference. The
  // input is only shown when the officer has call signs enabled and one set
  // in preferences.
  const { reportPreferences: userReportPreferences } = useAuth();
  const preferenceCallSign =
    (userReportPreferences?.include_call_sign &&
      userReportPreferences?.call_sign) ||
    "";
  const [callSign, setCallSign] = useState(preferenceCallSign);

  // reportPreferences loads async from AuthContext — populate the input with
  // the saved call sign once it arrives (not just as a placeholder)
  useEffect(() => {
    setCallSign(preferenceCallSign);
  }, [preferenceCallSign]);

  // Shared with UploadModal so the last-picked detail level is the default
  // everywhere
  const [detailLevel, setDetailLevel] = useLocalStorageState<
    "detailed" | "standard" | "concise"
  >("pnai-reportDetailLevel", "standard");

  // Re-mount TemplateSelector when dialog opens (picks up default changes from settings)
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      if (dialog.hasAttribute("open")) {
        setSelectedTemplateId(null);
        setTemplateKey((k) => k + 1);
        setCallSign(preferenceCallSign);
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, preferenceCallSign]);

  const recorder = useAudioRecorder();
  const editor = useAudioEditor();

  const isRecording =
    recorder.status === "recording" || recorder.status === "paused";
  const hasAudio = editor.audioData !== null;

  const recorderRef = useRef(recorder);
  const editorRef2 = useRef(editor);
  recorderRef.current = recorder;
  editorRef2.current = editor;

  const cleanup = useCallback(() => {
    recorderRef.current.resetRecording();
    editorRef2.current.reset();
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const handleClose = () => {
      setShowCloseConfirm(false);
      setShowClearConfirm(false);
      cleanup();
    };

    const handleCancel = (e: Event) => {
      if (
        editorRef2.current.audioData ||
        recorderRef.current.status !== "idle"
      ) {
        e.preventDefault();
        setShowCloseConfirm(true);
      } else {
        cleanup();
      }
    };

    modal.addEventListener("close", handleClose);
    modal.addEventListener("cancel", handleCancel);
    return () => {
      modal.removeEventListener("close", handleClose);
      modal.removeEventListener("cancel", handleCancel);
    };
  }, [modalRef, cleanup]);

  const handleRecord = useCallback(async () => {
    // Save cursor position before recording starts (waveform unmounts during recording, resetting cursor)
    if (editorRef2.current.audioData) {
      const cursorTime = editorRef.current?.getCurrentTime() ?? 0;
      const duration = editorRef2.current.getDuration();
      // Insert at cursor unless it's at the very end (within 5% of duration)
      insertAtRef.current =
        duration > 0 && cursorTime < duration * 0.95 ? cursorTime : null;
    }
    await recorderRef.current.startRecording();
  }, []);

  const handleStopRecording = useCallback(async () => {
    const buffer = await recorderRef.current.stopRecording();
    if (buffer) {
      if (editorRef2.current.audioData) {
        const insertAt =
          insertAtRef.current ?? editorRef2.current.getDuration();
        insertAtRef.current = null;
        editorRef2.current.insertAudioAt(buffer, insertAt);
      } else {
        insertAtRef.current = null;
        editorRef2.current.setAudioBuffer(buffer);
      }
    }
  }, []);

  const handleUpload = useCallback(async () => {
    const wavBlob = editorRef2.current.toWavBlob();
    if (!wavBlob) return;

    // Validate before closing so audio is preserved on failure
    if (!validateAudioFile(wavBlob, "narrative")) return;

    closeButtonRef.current?.click();
    cleanup();

    if (addToUploadMode && onAddToUpload) {
      onAddToUpload(wavBlob);
    } else {
      await handleAudioFileChange(
        wavBlob,
        "narrative",
        showReportSettings
          ? {
              method: "recording",
              speakers: [],
              additionalContext: "",
              includeTimestamps,
              detailLevel,
              callSign: preferenceCallSign ? callSign.trim() || null : null,
            }
          : "recording",
        selectedTemplateId,
      );
    }
  }, [
    cleanup,
    handleAudioFileChange,
    selectedTemplateId,
    addToUploadMode,
    onAddToUpload,
    showReportSettings,
    includeTimestamps,
    detailLevel,
    callSign,
    preferenceCallSign,
  ]);

  // Save to Files asks for a name first. The default is the current date and
  // time on this computer, so it is useful even if the officer types nothing.
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  // What the recording is. Narrative and interaction start transcribing right
  // away (saved on the file itself); Other is just stored.
  const [saveType, setSaveType] = useState<SaveAudioType>("narrative");
  const openSaveDialog = useCallback(() => {
    const wavBlob = editorRef2.current.toWavBlob();
    if (!wavBlob) return;
    if (!validateAudioFile(wavBlob, "narrative")) return;
    setSaveName(defaultRecordingName());
    setSaveType("narrative");
    setShowSaveDialog(true);
  }, []);
  const confirmSaveToFiles = useCallback(() => {
    const wavBlob = editorRef2.current.toWavBlob();
    if (!wavBlob || !onSaveToFiles) return;
    setShowSaveDialog(false);
    closeButtonRef.current?.click();
    cleanup();
    onSaveToFiles(wavBlob, saveName.trim() || defaultRecordingName(), saveType);
  }, [cleanup, onSaveToFiles, saveName, saveType]);

  const handleSuggestionsUpload = useCallback(async () => {
    const wavBlob = editorRef2.current.toWavBlob();
    if (!wavBlob) return;

    if (!validateAudioFile(wavBlob, "narrative")) return;

    // Close the modal immediately — the editor's "Adding to Report" overlay takes over.
    // Hand the recording off to the parent (Suggestions.tsx), which routes it through
    // the unified processAudioFile pipeline.
    closeButtonRef.current?.click();
    cleanup();
    onSuggestionsRecorded?.(wavBlob, selectedTemplateId);
  }, [cleanup, selectedTemplateId, onSuggestionsRecorded]);

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const dismissCloseConfirm = useCallback(() => {
    const el = closeConfirmRef.current;
    if (el) {
      el.style.animation = "fadeSlideRight 0.2s ease-in forwards";
      setTimeout(() => setShowCloseConfirm(false), 200);
    } else {
      setShowCloseConfirm(false);
    }
  }, []);

  const dismissClearConfirm = useCallback(() => {
    const el = clearConfirmRef.current;
    if (el) {
      el.style.animation = "fadeSlideRight 0.2s ease-in forwards";
      setTimeout(() => setShowClearConfirm(false), 200);
    } else {
      setShowClearConfirm(false);
    }
  }, []);

  const handleClearAll = useCallback(() => {
    recorderRef.current.resetRecording();
    editorRef2.current.reset();
    setShowClearConfirm(false);
  }, []);

  const handleCloseClick = useCallback(() => {
    if (editorRef2.current.audioData || recorderRef.current.status !== "idle") {
      setShowCloseConfirm(true);
    } else {
      closeButtonRef.current?.click();
    }
  }, []);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    cleanup();
    closeButtonRef.current?.click();
  }, [cleanup]);

  const headerBlock = (
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-3">
        <h3 className="font-bold text-lg">
          {suggestionsMode ? "Add to Report" : "Record Audio"}
        </h3>
        {isRecording && (
          <div className="flex items-center gap-1.5 bg-red-500/[0.08] border border-red-500/15 rounded-full py-1 pl-2 pr-2.5">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full ap-rec-dot mb-[0.35]" />
            <code className="text-[0.6875rem] font-medium text-red-600 dark:text-red-400 tabular-nums leading-none">
              {moment.utc(recorder.timer * 1000).format("mm:ss")}
            </code>
          </div>
        )}
      </div>
      {/* Hidden form button for programmatic close */}
      <form method="dialog" style={{ display: "none" }}>
        <button ref={closeButtonRef} />
      </form>
      <div className="flex items-center gap-2">
        {showCloseConfirm && (
          <div
            ref={closeConfirmRef}
            className="flex items-center gap-1.5 animate-[fadeSlideLeft_0.2s_ease-out]"
          >
            <span className="text-xs text-slate-500 dark:text-base-content/60 whitespace-nowrap">
              Discard audio?
            </span>
            <Button
              variant="primary"
              size="xs"
              className="!bg-red-500 hover:!bg-red-600"
              onClick={handleConfirmClose}
            >
              Yes
            </Button>
            <Button variant="outline" size="xs" onClick={dismissCloseConfirm}>
              No
            </Button>
          </div>
        )}
        <button onClick={handleCloseClick}>
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
    </div>
  );

  return (
    <dialog id="my_modal_1" className="modal" ref={modalRef}>
      <div
        // No !overflow-visible: it defeated .modal-box's overflow-y:auto, so
        // once the content passed max-height (calc(100vh - 5em) — reached
        // easily at 150% browser zoom, where Report Settings alone pushes the
        // content to ~680px in a ~490px box) it spilled out of the box and the
        // Upload button became unreachable. The only thing that needs to
        // escape the box is TemplateSelector's menu, and that already renders
        // through a portal with position:fixed.
        className={cn("modal-box ap-modal", {
          "max-w-5xl w-[80vw]": pdfUrl,
          "max-w-3xl": !pdfUrl,
          "ap-modal--recording": recorder.status === "recording",
          "ap-modal--paused": recorder.status === "paused",
        })}
      >
        <div className={cn(pdfUrl && "flex flex-col gap-4")}>
          {pdfUrl && headerBlock}
          {pdfUrl && (
            <div className="flex items-start gap-2.5 rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/40 px-3.5 py-2.5 -mt-1">
              <MicrophoneIcon className="size-4 text-blue-500 dark:text-blue-300 mt-0.5 shrink-0" />
              <div className="text-[0.8125rem] leading-snug text-slate-600 dark:text-base-content/70">
                <span className="font-medium text-slate-700 dark:text-base-content/80">
                  Fill out the form using your voice.
                </span>{" "}
                Read through the form below and describe the details for each
                field out loud as you go. When you're done, we'll transcribe
                your recording and automatically place the information into the
                matching fields.
              </div>
            </div>
          )}
          {pdfUrl && (
            <div className="w-full">
              <RecordPdfPanel pdfUrl={pdfUrl} />
            </div>
          )}
          <div className={cn(pdfUrl && "min-w-0")}>
          {!pdfUrl && headerBlock}

          {/* Waveform area */}
          <div
            className={cn(
              "bg-base-200 rounded-lg min-h-[180px] overflow-hidden border border-base-300 transition-colors duration-300",
              isRecording ? "border-red-500/30" : "border-slate-200 dark:border-base-300",
            )}
          >
            {isRecording ? (
              <RecordingVisualizer
                analyserNode={recorder.analyserNode}
                isPaused={recorder.status === "paused"}
                height={180}
              />
            ) : hasAudio ? (
              <div className="min-h-[180px]" ref={setWaveformContainer} />
            ) : (
              <div className="flex items-center justify-center min-h-[180px]">
                <p className="text-[0.8125rem] text-slate-400 dark:text-base-content/40 m-0">
                  {recorder.hasPermission
                    ? "Press record to begin capturing audio"
                    : "Grant microphone access to start recording"}
                </p>
              </div>
            )}
          </div>

          {/* Editor toolbar */}
          <AudioWaveformEditor
            ref={editorRef}
            audioData={editor.audioData}
            version={editor.version}
            onDeleteRegion={editor.deleteRegion}
            onMoveRegion={editor.moveRegion}
            onUndo={editor.undo}
            onRedo={editor.redo}
            canUndo={editor.canUndo}
            canRedo={editor.canRedo}
            duration={editor.getDuration()}
            isRecording={isRecording}
            containerElement={
              hasAudio && !isRecording ? waveformContainer : null
            }
          />

          {/* Suggestions list — visible in suggestionsMode (Add to Report >
              Record Audio) and in addToUploadMode when the parent passes
              suggestions/placeholders (Add to Report > Upload Audio > Record). */}
          {(suggestionsMode || addToUploadMode) &&
            ((suggestionsList && suggestionsList.length > 0) ||
              (placeholders && placeholders.length > 0)) && (
            <div className="mb-2 max-h-[280px] overflow-y-scroll rounded-lg border border-base-300 bg-base-100">
              <div className="px-3 py-2 border-b border-slate-100 dark:border-base-300 sticky top-0 bg-base-100 z-10">
                <p className="text-xs font-medium text-slate-500 dark:text-base-content/60 m-0">
                  Record supplemental audio and we'll intelligently integrate
                  new details into your report. Suggested points to cover:
                </p>
              </div>
              <div className="px-3 py-2 flex flex-col gap-1.5">
                {(suggestionsList || [])
                  .filter((s) => !s.implemented)
                  .map((s) => (
                    <div
                      key={s.id}
                      className="flex items-start gap-2 text-sm text-slate-600 dark:text-base-content/70"
                    >
                      <CheckCircleIcon className="size-3.5 text-blue-400 mt-0.5 shrink-0" />
                      <SuggestionPoint content={s.content} />
                    </div>
                  ))}
                {(placeholders || []).map((p, i) => (
                  <div
                    key={`ph-${i}`}
                    className="flex items-start gap-2 text-sm text-slate-600 dark:text-base-content/70"
                  >
                    <span className="text-amber-500 mt-0.5 shrink-0 text-xs font-mono">
                      {"{ }"}
                    </span>
                    <span className="font-mono text-xs bg-amber-50 px-1 py-0.5 rounded">
                      {p}
                    </span>
                  </div>
                ))}
                {(suggestionsList || []).filter((s) => !s.implemented)
                  .length === 0 &&
                  (placeholders || []).length === 0 && (
                    <p className="text-xs text-slate-400 dark:text-base-content/40 m-0">
                      No suggestions or placeholders to address.
                    </p>
                  )}
              </div>
            </div>
          )}

          {/* Device selector */}
          {recorder.hasPermission && recorder.devices.length > 0 && (
            <select
              className="ap-device-select mb-2"
              onChange={(e) => recorder.setSelectedDeviceId(e.target.value)}
              value={recorder.selectedDeviceId}
              disabled={isRecording}
            >
              {recorder.devices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          )}

          {/* Recording controls: right under the waveform, above the template and
              report settings */}
          <div className="ap-actions mb-3">
              {!recorder.hasPermission ? (
                <div className="ap-actions-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={recorder.requestPermission}
                  >
                    <MicrophoneIcon className="size-4" />
                    <span>Grant Microphone Access</span>
                  </Button>
                </div>
              ) : isRecording ? (
                <div className="ap-actions-center">
                  {recorder.status === "paused" ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={recorder.resumeRecording}
                    >
                      <PlayIcon className="size-4" />
                      <span>Resume</span>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={recorder.pauseRecording}
                    >
                      <PauseIcon className="size-4" />
                      <span>Pause</span>
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    className="!bg-red-500 hover:!bg-red-600"
                    onClick={handleStopRecording}
                  >
                    <StopIcon className="size-4" />
                    <span>Stop Recording</span>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="ap-actions-center">
                    <Button variant="danger" size="sm" onClick={handleRecord}>
                      <span className="w-2 h-2 bg-red-500 rounded-full shrink-0" />
                      <span>{hasAudio ? "Record at Cursor" : "Record"}</span>
                    </Button>

                    {hasAudio && (
                      <>
                        {showClearConfirm ? (
                          <div
                            ref={clearConfirmRef}
                            className="flex items-center gap-2 animate-[fadeSlideLeft_0.2s_ease-out]"
                          >
                            <span className="text-xs text-slate-500 dark:text-base-content/60 whitespace-nowrap">
                              Clear all audio?
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="primary"
                                size="xs"
                                className="!bg-red-500 hover:!bg-red-600"
                                onClick={handleClearAll}
                              >
                                Yes
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={dismissClearConfirm}
                              >
                                No
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowClearConfirm(true)}
                          >
                            <TrashIcon className="size-4" />
                            <span>Clear</span>
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                </>
              )}
          </div>

          {/* Template selector — hidden in form-fill mode (pdfUrl), which always
              submits with no report template */}
          {hasAudio &&
            !isRecording &&
            !addToUploadMode &&
            !showReportSettings &&
            !pdfUrl && (
            <div className="mb-2">
              <TemplateSelector
                key={templateKey}
                selectedTemplateId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
                autoSelectDefault={!suggestionsMode}
              />
            </div>
          )}

          {/* Bottom controls */}
          <div className="flex flex-col gap-2.5">
            {/* Report Settings accordion — mirrors UploadModal */}
            {hasAudio &&
              !isRecording &&
              !addToUploadMode &&
              showReportSettings && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    className="flex items-center justify-between w-full p-2 text-left text-sm font-medium text-gray-700 dark:text-base-content/80"
                  >
                    Report Settings
                    {showAdvancedOptions ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>

                  <div
                    className={cn(
                      "transition-all duration-300 ease-in-out overflow-hidden",
                      showAdvancedOptions
                        ? "max-h-[500px] opacity-100"
                        : "max-h-0 opacity-0",
                    )}
                  >
                    <div className="mt-2 space-y-4 p-4 bg-base-200 rounded-lg">
                      {/* Report Template */}
                      <TemplateSelector
                        key={templateKey}
                        selectedTemplateId={selectedTemplateId}
                        onSelect={setSelectedTemplateId}
                        autoSelectDefault={!suggestionsMode}
                      />

                      {/* Call Sign Override — only when a call sign is set in preferences */}
                      {preferenceCallSign && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                            Call Sign for This Report
                          </label>
                          <input
                            type="text"
                            value={callSign}
                            onChange={(e) => setCallSign(e.target.value)}
                            placeholder={preferenceCallSign}
                            className="px-3 py-2 text-sm border border-gray-300 dark:border-base-300 rounded-lg w-full bg-base-100"
                          />
                          <p className="mt-1 text-xs text-base-content/60">
                            Defaults to your saved call sign. Adjust it if
                            you're on a different shift or assignment for this
                            report.
                          </p>
                        </div>
                      )}

                      {/* Detail Level Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                          Report Detail Level
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            {
                              value: "concise",
                              label: "Concise",
                              desc: "Brief summary",
                            },
                            {
                              value: "standard",
                              label: "Standard",
                              desc: "Balanced detail",
                            },
                            {
                              value: "detailed",
                              label: "Detailed",
                              desc: "Comprehensive",
                            },
                          ].map((option) => (
                            <Button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setDetailLevel(option.value as any)
                              }
                              variant={
                                detailLevel === option.value
                                  ? "secondary"
                                  : "outline"
                              }
                              className={cn(
                                "w-full !border h-auto",
                                detailLevel === option.value &&
                                  "border-blue-500",
                                "p-3",
                              )}
                              size="sm"
                            >
                              <div>
                                <div className="font-medium">
                                  {option.label}
                                </div>
                                <div className="text-xs font-normal">
                                  {option.desc}
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Timestamps Toggle */}
                      <div>
                        <Toggle
                          label="Include timestamps in narrative"
                          checked={includeTimestamps}
                          onChange={(e) =>
                            setIncludeTimestamps(e.target.checked)
                          }
                          labelSize="sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Save / Upload live alone at the bottom */}
            {recorder.hasPermission && !isRecording && hasAudio && (
            <div className="ap-actions">
              <div className="ap-actions-right">
                {onSaveToFiles && !addToUploadMode && !suggestionsMode && (
                  <Button variant="secondary" size="sm" onClick={openSaveDialog}>
                    <FolderArrowDownIcon className="size-4" />
                    <span>Save to Files</span>
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={
                    suggestionsMode
                      ? handleSuggestionsUpload
                      : handleUpload
                  }
                >
                  <ArrowUpTrayIcon className="size-4" />
                  <span>
                    {suggestionsMode
                      ? "Apply to Report"
                      : addToUploadMode
                        ? "Add to Upload"
                        : "Upload"}
                  </span>
                </Button>
              </div>
            </div>
            )}
          </div>
          </div>
        </div>
      </div>
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-base-100 border border-base-300 p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-base-content">Save to Files</h3>
            <p className="text-sm text-base-content/60 mt-1">Name this recording and say what it is.</p>
            <input
              autoFocus
              type="text"
              value={saveName}
              maxLength={150}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmSaveToFiles();
                if (e.key === "Escape") setShowSaveDialog(false);
              }}
              className="mt-3 px-3 py-2 text-sm border border-gray-300 dark:border-base-300 rounded-lg w-full bg-base-100"
            />
            <div className="mt-4">
              <div className="text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1.5">Type</div>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { value: "narrative", label: "Narrative", desc: "Your dictation" },
                    { value: "interaction", label: "Interaction", desc: "An interview" },
                    { value: "other", label: "Other", desc: "Don't transcribe" },
                  ] as const
                ).map((o) => (
                  <Button
                    key={o.value}
                    type="button"
                    size="sm"
                    variant={saveType === o.value ? "secondary" : "outline"}
                    className={cn("w-full !border h-auto p-2", saveType === o.value && "border-blue-500")}
                    onClick={() => setSaveType(o.value)}
                  >
                    <div>
                      <div className="font-medium">{o.label}</div>
                      <div className="text-[11px] font-normal">{o.desc}</div>
                    </div>
                  </Button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-base-content/50">
                {saveType === "other"
                  ? "Saved as is. You can transcribe it later from Files."
                  : "Transcription starts as soon as it is saved, and the transcript stays with the file."}
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={confirmSaveToFiles}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </dialog>
  );
}
